import { FC, useEffect, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { bugoDataAtomFamily } from '@shared/api/bugo/bugo.atom';
import {
  clearGiftAgentOrder,
  getGiftAgentOrder,
  restoreAdminSessionFromAgentOrder,
} from '@shared/utils/giftAgentOrder.util';
import { getReverseType } from '@shared/utils/memberUtils';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useLocation } from 'react-router-dom';

import { GiftAgentOrderResetButton } from './GiftAgentOrderResetButton';

/**
 * 대리주문 모드 표시 배너 (스토어 전 페이지 공통, NavBar 아래 우측 고정)
 *
 * - 지금 어느 상주 명의로, 어느 상가(고인·관계) 주문인지 상시 노출해 혼동을 막는다.
 * - "주문서 정리": 상가 주문 내역 패널과 섞이지 않도록 배너 바로 아래 둔다.
 * - "종료" 버튼: 결제를 진행해 게스트로 전환된 상태면 보관해 둔 관리자 세션을
 *   복원하고, 전환 전이면 컨텍스트만 지운 뒤 상주검색 페이지로 돌아간다.
 *   (recoil atom이 게스트 세션을 들고 있어 전체 리로드로 이동한다)
 */
export const GiftAgentOrderBanner: FC = function GiftAgentOrderBanner() {
  const location = useLocation();
  const [ctx, setCtx] = useState(() => getGiftAgentOrder());
  //* 대리주문 시작 시점에 컨텍스트로 스냅샷해 둔 상가 정보가 없을 때(배포 전에
  //* 시작된 주문 등)의 폴백. 스토어 게이트가 이미 불러온 캐시라 추가 요청은 없다.
  const bugo = useRecoilValue(bugoDataAtomFamily(ctx?.bugoId ?? ''));

  // 결제창 왕복·페이지 이동 후에도 컨텍스트 유무를 다시 확인
  useEffect(() => {
    setCtx(getGiftAgentOrder());
  }, [location]);

  if (!ctx) return null;

  const deceasedName = ctx.deceasedName ?? bugo?.fevent?.deceasedInfo?.name;
  const relationType = ctx.relationType ?? bugo?.member?.relationType;
  const deceasedSex = bugo?.fevent?.deceasedInfo?.sex;
  const reverseType =
    ctx.reverseType ??
    (relationType && deceasedSex ? getReverseType(relationType, deceasedSex) : undefined);

  //* 어느 상가인지 — "故 OOO님 · 부친상(자)". 역관계가 없으면 관계만 표기한다.
  const relationLabel = reverseType
    ? `${reverseType}상${relationType ? `(${relationType})` : ''}`
    : relationType;
  const feventLabel = [deceasedName ? `故 ${deceasedName}님` : null, relationLabel]
    .filter(Boolean)
    .join(' · ');

  const handleExit = () => {
    // 결제를 진행해 게스트로 전환됐다면 보관해 둔 관리자 로그인까지 함께 복원된다
    const confirmed = window.confirm('전화주문을 종료하고 관리자 화면으로 돌아갑니다.');
    if (!confirmed) return;
    restoreAdminSessionFromAgentOrder();
    clearGiftAgentOrder();
    window.location.href = '/gift-seller/phone-order';
  };

  return (
    <div className="fixed top-8 right-2 z-40 flex flex-col items-end gap-1.5 lg:w-96 lg:items-stretch">
      <div className="flex items-center gap-2 rounded-lg bg-indigo-700/95 px-3 py-2 text-white shadow-lg">
        <div className="flex-1 text-xs leading-tight">
          <p className="font-semibold">전화주문 진행 중</p>
          <p className="text-lg font-medium">
            {ctx.memberName}님 ({phoneNumberStringFormator(ctx.memberPhone)})
          </p>
          {feventLabel && <p className="text-indigo-100">{feventLabel}</p>}
        </div>
        <button
          onClick={handleExit}
          className="rounded-md bg-white px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
        >
          종료
        </button>
      </div>
      {/* 주문서 정리 — 상가 주문 내역(회색 패널)과 헷갈리지 않도록 배너에 붙인다.
          좁은 화면에선 주문 내역 오버레이 안쪽 버튼을 쓴다 */}
      <GiftAgentOrderResetButton ctx={ctx} className="hidden shadow-lg lg:block" />
    </div>
  );
};
